import { createContext, useState } from 'react';
import type { ReactNode } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { userService } from '../../../shared/services/users/usersServices';
import { userAttributesService } from '../../../shared/services/userAtributes/userAtributes';
import { format } from 'date-fns';
import { roleService } from '../../../shared/services/roles/roleService';

export interface User {
  id: string;
  name: string;
  email: string;
  role: string;
  status: string;
  documentNumber?: string;
  phoneNumber?: string;
  description?: string;
  imageUrl?: string;
  createdAt?: string;
  updatedAt?: string;
  attributes?: Array<{
    key: string;
    value: string;
  }>;
  tenants: {
    id: string;
    name: string;
    role: string;
  }[];
}

interface UserContextData {
  // Listagem
  users: User[];
  isLoading: boolean;
  loading: boolean;
  error: string | null;
  handleCreate: () => void;
  handleView: (user: User) => void;
  handleCloseError: () => void;
  
  // Modal de Delete
  isDeleteModalOpen: boolean;
  selectedUser: User | null;
  openDeleteModal: (user: User) => void;
  closeDeleteModal: () => void;
  handleDeleteUser: () => void;
  isDeleting: boolean;
  
  // Navegação
  navigateToEdit: (id: string) => void;

  // Form
  userData: User | null;
  isDataLoading: boolean;
  createUser: (data: any) => Promise<void>;
  updateUser: (id: string, data: any) => Promise<void>;
  isSubmitting: boolean;
  getUserById: (id: string) => User;
  setError: (error: string | null) => void;
}

interface UserProviderProps {
  children: ReactNode;
}

export const UserContext = createContext<UserContextData>({} as UserContextData);

export function UserProvider({ children }: UserProviderProps) {
  console.log('UserProvider montou');
  const navigate = useNavigate();
  const { id } = useParams();
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);

  // Query para listar usuários
  const { data: users = [], isLoading } = useQuery({
    queryKey: ['users'],
    queryFn: async () => {
      try {
        const response = await userService.list();
        return response.data;
      } catch (error: any) {
        if (typeof error === 'object' && error !== null) {
          const errorMessage = error.response?.data?.message || error.message || 'Erro ao buscar usuários';
          setError(errorMessage);
        } else {
          setError('Erro ao buscar usuários');
        }
        return [];
      }
    }
  });

  // Query para buscar dados do usuário no modo de edição
  const { data: userData = null, isLoading: isDataLoading } = useQuery({
    queryKey: ['user', id],
    queryFn: async () => {
      if (!id) return null;
      try {
        const response = await userService.get(id);
        return response.data;
      } catch (error: any) {
        if (typeof error === 'object' && error !== null) {
          const errorMessage = error.response?.data?.message || error.message || 'Erro ao buscar usuário';
          setError(errorMessage);
        } else {
          setError('Erro ao buscar usuário');
        }
        return null;
      }
    },
    enabled: !!id,
  });

  // Estado do modal de delete
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);

  // Mutation para deletar usuário
  const deleteMutation = useMutation({
    mutationFn: (id: string) => userService.remove(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      closeDeleteModal();
      navigate('/users');
    }
  });

  // Handlers para listagem
  const handleCreate = () => {
    navigate('/users/new');
  };

  const handleView = (user: User) => {
    navigate(`/users/${user.id}`);
  };

  const handleCloseError = () => {
    setError(null);
  };

  // Handlers para modal de delete
  const openDeleteModal = (user: User) => {
    setSelectedUser(user);
    setIsDeleteModalOpen(true);
  };

  const closeDeleteModal = () => {
    setIsDeleteModalOpen(false);
    setSelectedUser(null);
  };

  const handleDeleteUser = () => {
    if (selectedUser?.id) {
      deleteMutation.mutate(selectedUser.id);
    }
  };

  // Handler para navegação
  const navigateToEdit = (id: string) => {
    navigate(`/users/${id}/edit`);
  };

  // Mutations para criar e atualizar usuários
  const createMutation = useMutation({
    mutationFn: async (data: any) => {
      console.log('teste01', data);
      const formData = new FormData();
      formData.append('name', data.name);
      formData.append('email', data.email);
      formData.append('status', data.status);
      formData.append('role', data.role);
      formData.append('documentNumber', data.documentNumber);
      formData.append('phoneNumber', data.phoneNumber);
      formData.append('description', data.description || 'Morador');
      formData.append('password', 'morador@123');

      if (data.tenants) {
        data.tenants.forEach((tenant: any, index: any) => {
          formData.append(`tenants[${index}][id]`, tenant.id);
          formData.append(`tenants[${index}][role]`, data.role);
        });
      }
      
      if (data.avatarFile) {
        formData.append('file', data.avatarFile);
      }

      const userResponse = await userService.create(formData);
      const userId = userResponse?.data?.id;

      // Criar atributos do usuário
      const attributesPayload = {
        userId,
        attributes: [
          { key: "status_flag", value: data.status_flag || "ACTIVE" },
          { key: "document", value: data.document || "" },
          { key: "nationality", value: data.nationality || "" },
          { key: "birth_date", value: data.birth_date ? format(new Date(data.birth_date), 'yyyy-MM-dd') : "" },
          { key: "gender_flag", value: data.gender_flag || null },
          { key: "marital_flag", value: data.marital_flag || null },
          { key: "type_flag", value: data.type_flag || "CONDOMINO" },
          { key: "phone_number", value: data.phone_number || "" },
          {
            key: "cnh",
            value: data.cnh_number && data.cnh_expiration_date
              ? `${data.cnh_number} - ${format(new Date(data.cnh_expiration_date), 'yyyy-MM-dd')}`
              : ""
          },
          ...(data.unit ? [{ key: "unit", value: data.unit }] : []),
          ...(data.section_name ? [{ key: "section_name", value: data.section_name }] : []),
        ]
      };

      await userAttributesService.create(attributesPayload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
    onError: (error: any) => {
      if (typeof error === 'object' && error !== null) {
        const errorMessage = error.response?.data?.message || error.message || 'Erro ao criar usuário';
        setError(errorMessage);
      } else {
        setError('Erro ao criar usuário');
      }
      throw error;
    }
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: any }) => {
      const userData: any = {
        name: data.name,
        email: data.email,
        description: data.description || 'Morador',
      };
      
      if (data.password) userData.password = data.password;
      
      await userService.update(id, userData);

      // Atualizar atributos do usuário
      const attributesPayload = {
        userId: id,
        attributes: [
          { key: "status_flag", value: data.status_flag || "ACTIVE" },
          { key: "document", value: data.document || "" },
          { key: "nationality", value: data.nationality || "" },
          { key: "birth_date", value: data.birth_date ? format(new Date(data.birth_date), 'yyyy-MM-dd') : "" },
          { key: "gender_flag", value: data.gender_flag || null },
          { key: "marital_flag", value: data.marital_flag || null },
          { key: "type_flag", value: data.type_flag || "CONDOMINO" },
          { key: "phone_number", value: data.phone_number || "" },
          {
            key: "cnh",
            value: data.cnh_number && data.cnh_expiration_date
              ? `${data.cnh_number} - ${format(new Date(data.cnh_expiration_date), 'yyyy-MM-dd')}`
              : ""
          },
          ...(data.unit ? [{ key: "unit", value: data.unit }] : []),
          ...(data.section_name ? [{ key: "section_name", value: data.section_name }] : []),
        ]
      };

      await userAttributesService.update(attributesPayload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      queryClient.invalidateQueries({ queryKey: ['user', id] });
    },
    onError: (error: any) => {
      if (typeof error === 'object' && error !== null) {
        const errorMessage = error.response?.data?.message || error.message || 'Erro ao atualizar usuário';
        setError(errorMessage);
      } else {
        setError('Erro ao atualizar usuário');
      }
      throw error;
    }
  });

  const createUser = async (data: any) => {
    await createMutation.mutateAsync(data);
  };

  const updateUser = async (id: string, data: any) => {
    await updateMutation.mutateAsync({ id, data });
  };

  const getUserById = (id: string) => {
    const user = users.find((u:any) => u.id === id);
    if (!user) {
      throw new Error('Usuário não encontrado');
    }
    return user;
  };

  return (
    <UserContext.Provider
      value={{
        // Listagem
        users,
        isLoading,
        loading: createMutation.isPending || updateMutation.isPending,
        error,
        handleCreate,
        handleView,
        handleCloseError,
        
        // Modal de Delete
        isDeleteModalOpen,
        selectedUser,
        openDeleteModal,
        closeDeleteModal,
        handleDeleteUser,
        isDeleting: deleteMutation.isPending,
        
        // Navegação
        navigateToEdit,

        // Form
        userData,
        isDataLoading,
        createUser,
        updateUser,
        isSubmitting: createMutation.isPending || updateMutation.isPending,
        getUserById,
        setError,
      }}
    >
      {children}
    </UserContext.Provider>
  );
}